import React, { useState, useContext, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import {
  MdVisibility,
  MdVisibilityOff,
  MdWarning,
  MdInfo,
} from "react-icons/md";
import "../../styles/Login.css";
import LoginService from "../../services/auth/LoginSerices";
import SettingServices from "../../services/SettingServices";
import { AuthContext } from "../../context/AuthContext";
import AppLogo from "../../components/AppLogo";

// Validation Schema
const loginValidationSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, "Username must be at least 3 characters")
    .required("Username is required"),
  password: Yup.string()
    .min(3, "Password must be at least 3 characters")
    .required("Password is required"),
});

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginNotes, setLoginNotes] = useState("");
  const { login } = useContext(AuthContext);
  const history = useHistory();

  const loginSer = new LoginService();
  const settingSer = new SettingServices();

  useEffect(() => {
    settingSer.getLoginNotes().then((res) => {
      if (res?.isSuccess && res?.data?.login_notes) {
        setLoginNotes(res.data.login_notes);
      }
    }).catch(() => {});
  }, []);

  const initialValues = {
    username: "",
    password: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    setLoginError("");

    try {
      const response = await loginSer.getLoginUserData(values);

      console.log("Login successful:", response);

      // Extract data from response
      const { token, result } = response;
      const userData = result;

      // Store in auth context
      login(token, userData);

      // Show success toast
      toast.success(`Welcome, ${userData.name}!`);

      // Redirect based on user role
      setTimeout(() => {
        if(userData.role_id === 1) {
          history.push("/live-session");
        }else if(userData.role_id === 4) {
          history.push("/mobile-live-session");
        } else if(userData.role_id === 3) {
          history.push("/outlet-live-session");
        } else {
          history.push("/dashboard");
        }
      }, 500);
    } catch (error) {
      console.error("Login failed:", error);

      const status = error?.response?.status;
      const msg = error?.response?.data?.error || "";
      const isDeactivatedAccount =
        status === 403 &&
        /inactive|account inactive|session invalid/i.test(String(msg));
      const isMobileOnlyBlocked =
        status === 403 &&
        /mobile users can login only on mobile device/i.test(String(msg));

      if (isDeactivatedAccount) {
        setLoginError("");
        setIsLoading(false);
        return;
      }
      if (isMobileOnlyBlocked) {
        setLoginError(msg || "Mobile users can login only on mobile device");
        toast.error(msg || "Mobile users can login only on mobile device");
        setIsLoading(false);
        return;
      }

      const errorMessage =
        error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "Login failed. Please check your credentials.";

      setLoginError(errorMessage);
      toast.error(errorMessage);
      setIsLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-brand">
          <AppLogo theme="light" />
        </div>
        {/* Header */}
        <div className="login-header">
          <h1>Login to Your Account</h1>
        </div>

        {/* Error Message */}
        {loginError && (
          <div className="error-alert">
            <MdWarning className="error-icon" />
            <span className="error-message">{loginError}</span>
          </div>
        )}

        {/* Formik Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={loginValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="login-form">
              {/* username Field */}
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  User Name <span className="required-asterisk">*</span>
                </label>
                <div className="input-wrapper">
                  <Field
                    type="username"
                    id="username"
                    name="username"
                    placeholder="Enter your username"
                    className={`form-input ${errors.username && touched.username ? "input-error" : ""}`}
                  />
                </div>
                <ErrorMessage name="username">
                  {(msg) => <div className="error-text">{msg}</div>}
                </ErrorMessage>
              </div>

              {/* Password Field */}
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password <span className="required-asterisk">*</span>
                </label>
                <div className="input-wrapper">
                  <Field
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    className={`form-input ${errors.password && touched.password ? "input-error" : ""}`}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </button>
                </div>
                <ErrorMessage name="password">
                  {(msg) => <div className="error-text">{msg}</div>}
                </ErrorMessage>
              </div>

              {/* Forgot Password Link */}
              <div className="form-footer">
                <a href="#forgot" className="forgot-password-link">
                  Forgot password?
                </a>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-button"
                disabled={isSubmitting || isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Signing in...
                  </>
                ) : (
                  "Login"
                )}
              </button>

              {/* Login Notes - bound after login button */}
              {loginNotes && (
                <div className="login-notes-section">                  
                  <div className="login-notes-content"><MdInfo className="login-notes-icon" /> {loginNotes}</div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Login;
